import { EditorState } from 'draft-js'
import { SyntheticEvent, useContext, useEffect } from 'react'
import { EditorContext } from '../EditorContext'
import { InputProps } from '../types/input'
import { ErrorType, validateEditorTextLength } from '../utils'

export const useInput = (
  args: Pick<InputProps, 'onChange' | 'maxPlainTextLength' | 'name' | 'required' | 'onError'>
) => {
  const { editorState, setEditorState, editorError, setEditorError, isControlled } =
    useContext(EditorContext)

  const editorPlainText = editorState.getCurrentContent().getPlainText()
  const maxPlainTextLength = args.maxPlainTextLength || Infinity

  const setMaxTextError = () => setEditorError(`Max ${maxPlainTextLength} characters`)
  const setRequiredError = () => setEditorError(`${args.name || 'Field'} is required`)

  const handleChange = (editorState: EditorState) => {
    if (!(args.required && editorState.getCurrentContent().getPlainText().length === 0)) {
      setEditorError(undefined)
    }

    setEditorState(editorState)

    if (isControlled && args.onChange) {
      args.onChange(editorState)
    }
  }

  const handlePastedText = (text: string, _html: string, editorState: EditorState) => {
    return validate(text, editorState)
  }

  const handleBeforeInput = (text: string, editorState: EditorState) => {
    return validate(text, editorState)
  }

  const handleBlur = (_e: SyntheticEvent) => {
    validate('', editorState)
  }

  const validate = (text: string, editorState: EditorState) => {
    const { status, errorType } = validateEditorTextLength({
      text,
      editorState,
      maxPlainTextLength,
      required: args.required
    })

    if (errorType === ErrorType.MAX_LENGTH) {
      setMaxTextError()
    } else if (errorType === ErrorType.REQUIRED) {
      setRequiredError()
    }

    return status
  }

  useEffect(() => {
    if (editorError && args.onError) {
      args.onError(editorError)
    }
  }, [editorError])

  return {
    editorState,
    editorError,
    editorPlainText,
    handleChange,
    handleBeforeInput,
    handlePastedText,
    handleBlur
  }
}
